import './index.css'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { getProvider as getVersionProvider } from '@molecule/app-version'

import { App } from './App.js'
import { authClient, setupProviders } from './bonds/index.js'
import { registerChatCustomEvents } from './chat-custom-events.js'
import { installChunkReloadHandler } from './lib/chunk-reload.js'
import { setupGlobalErrorReporting } from './utilities/error-reporting.js'
import { startFreezeRecorder } from './utilities/freeze-recorder.js'
import { applySoftwareGpuMode } from './utilities/software-gpu.js'

// Software-rendered browsers (VMs, RDP, blocklisted GPUs) get decorative
// animations paused — a saturated software GPU process can hang the whole tab.
// Applied before first render so the animations never start there.
applySoftwareGpuMode()

// Freeze flight recorder. A frozen tab is a blocked main thread; this persists
// heap/DOM/stall/lifecycle breadcrumbs to localStorage so the state right before
// a freeze survives a tab kill, and logs the previous session's freeze on load —
// distinguishing a memory blow-up from a CPU loop from a browser-level wedge.
// Always on in dev; in production it is opt-in per browser (set localStorage
// `mol-freeze:enable` = '1' and reload) so affected users can capture a
// recurring hang without any cost to everyone else.
const shouldRecordFreezes = (): boolean => {
  if (import.meta.env.DEV) return true
  try {
    return localStorage.getItem('mol-freeze:enable') === '1'
  } catch (_error) {
    // localStorage unavailable (privacy mode) — no way to opt in, stay off.
    return false
  }
}
if (shouldRecordFreezes()) {
  startFreezeRecorder()
}

setupProviders()

// Report the REAL build version (baked in at build time by vite's `define`) to the
// version provider, so useVersion(), the /version command, and bug-report
// diagnostics show the running build instead of an empty string. Set before render
// so useVersion's first snapshot already carries it. The version string is the
// package semver plus the short commit (e.g. "0.0.1+a1b2c3d") when git is available.
getVersionProvider().setCurrentVersion({
  buildId: __BUILD_COMMIT__ || __BUILD_VERSION__ || 'dev',
  version: __BUILD_COMMIT__ ? `${__BUILD_VERSION__}+${__BUILD_COMMIT__}` : __BUILD_VERSION__,
  buildTime: __BUILD_TIME__,
  ...(__BUILD_COMMIT__ ? { commitHash: __BUILD_COMMIT__ } : {}),
})

// OBS1: forward uncaught window errors + unhandled promise rejections to the
// first-party analytics endpoint (React render crashes go through ErrorBoundary).
// Wired after setupProviders so the HTTP client the reporter uses is configured.
setupGlobalErrorReporting()

// Register molecule.dev's app-specific chat stream events (e.g. build_degraded)
// with the shared ChatPanel's custom-event registry — keeps these specifics out of
// the core @molecule/app-* packages.
registerChatCustomEvents()

authClient.initialize().catch(() => {})

// A tab that outlived a deploy asks for chunk names the host no longer serves —
// and a mobile tab resumed after hours away is exactly that tab. Reload ONCE so
// it picks up the current asset graph (guarded so a broken deploy can't loop);
// lazy loaders wait out the reload instead of crashing (lib/chunk-reload.ts).
installChunkReloadHandler()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// Service worker policy:
//   - Pre-launch static landing (VITE_PWA unset): register nothing here, and
//     evict any SW a previous build left behind — caching SWs serve stale
//     builds, and "empty cache and reload" doesn't remove them. The build also
//     emits a self-destroying /sw.js to clean up old registrations.
//   - PWA build (VITE_PWA=true): the app enrolls users into the PWA on demand
//     when they open the IDE (see WorkspaceRoute), NOT on the landing — so
//     casual/bounce visitors stay service-worker-free. Nothing to do on load.
if (import.meta.env.VITE_PWA !== 'true' && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.getRegistrations().then((regs) => {
    if (!regs.length) return
    void Promise.all(regs.map((r) => r.unregister())).then(() => {
      if (window.caches) {
        void caches.keys().then((keys) => keys.forEach((k) => void caches.delete(k)))
      }
      // If a stale SW was still controlling this page, reload once for fresh assets.
      if (navigator.serviceWorker.controller) window.location.reload()
    })
  })
}
